import { TAX_APPS_IMPORT_OPTIONS } from '@st/tax-folder'
import { Button, Wizard, WizardIcons } from '@st/theme'
import { match, P } from '@st/pattern'
import { captureUpload } from '@st/util/archive/browser-upload'
import { useCaptureBrowserResource } from '~/features/browser-resources/browser-resource-boundary'
import { clientPacketsFlowDefinition } from '~/features/client-packets/client-packets-flow'
import { useProcess, useSelect } from '~/store'
import {
  type CreateFolderImportMessage,
  type CreateFolderImportState,
  type CreateFolderImportUploadFile,
  importTaxYears,
  requestFolderImport
} from './create-folder-import-module'
import { UploadFolderImportWizard } from './create-folder-import-upload-file-wizard'
import { FolderCreateOneByOnePage } from './folder-create-one-by-one-page'
import { TaxYearSelector } from './select-binder-tax-year-page'
import { ProConnectImportPage } from './pro-connect-import-page'

type Props = {
  onClose: () => void
}
/**
 * Import is run in 2 steps:
 * 1. Create the import (including creating an import with a unique id and uploading a source backup file)
 * 2. Create folders from the import
 *
 * This page is responsible for step 1.
 */
export function CreateFolderImportPage({ onClose }: Props) {
  // Mode is derived from the route inside `createFolderImportModule` (Elm-style: routing is input).
  const folderImport = useProcess('createFolderImport')
  const state = useSelect('createFolderImport', (s) => s)

  return match(state)
    .with({ step: 'idle' }, () => null)
    .with({ step: 'newOrReturning' }, (state) => (
      <FolderImportSelectNewOrReturning state={state} send={folderImport.send} onClose={onClose} />
    ))
    .with({ step: 'taxAppSelector' }, (state) => (
      <FolderImportTaxAppSelector state={state} send={folderImport.send} onClose={onClose} />
    ))
    .with({ step: 'newClientsImportMethodSelector' }, (state) => (
      <SelectAddNewClientsMethod
        state={state}
        send={folderImport.send}
        onBack={() => {
          folderImport.send({ type: 'backRequested' })
        }}
        onClose={onClose}
      />
    ))
    .with({ step: 'taxYearSelector' }, (state) => (
      <TaxYearSelector
        years={importTaxYears(state.taxApp).map((sourceTaxYear) => sourceTaxYear + 1)}
        onClose={onClose}
        onBack={() => folderImport.send({ type: 'backRequested' })}
        onTaxYearSelected={(binderTaxYear) =>
          folderImport.send({ type: 'taxYearSelected', taxYear: binderTaxYear - 1 })
        }
      />
    ))
    .with({ step: 'proConnectImport' }, (state) => (
      <ProConnectImportPage
        state={state.proConnect}
        send={(message) => folderImport.send({ type: 'proConnect', message })}
        onBack={() => folderImport.send({ type: 'backRequested' })}
        onClose={onClose}
      />
    ))
    .with({ step: 'uploadImportFile' }, (state) => (
      <UploadFolderImport
        key={state.routePath}
        state={state}
        send={folderImport.send}
        onClose={onClose}
      />
    ))
    .with({ step: 'createOneByOne' }, (state) => (
      <FolderCreateOneByOnePage
        state={state.createOneByOne}
        send={(message) => folderImport.send({ type: 'createOneByOne', message })}
        onBack={() => folderImport.send({ type: 'backRequested' })}
        onClose={onClose}
      />
    ))
    .exhaustive()
}

type PageProps<Step extends CreateFolderImportState['step']> = {
  state: Extract<CreateFolderImportState, { step: Step }>
  send: (message: CreateFolderImportMessage) => void
  onBack?: () => void
  onClose?: () => void
}
function FolderImportSelectNewOrReturning({ state, send, onClose }: PageProps<'newOrReturning'>) {
  return (
    <Wizard.Scaffold breadcrumb="Generate binders" onClose={onClose}>
      <Wizard.Main
        title={
          state.mode == 'preview'
            ? 'Do you want to preview a binder for a new client or returning client?'
            : 'Do you want to generate binders for new clients or returning clients?'
        }
        subtitle={
          state.mode == 'preview'
            ? "You can preview binders for as many clients as you'd like for free."
            : "You can generate as many binders as you'd like for free. You'll only have to pay for a binder if documents are uploaded to it."
        }
      >
        <Wizard.OptionsColumn>
          <Wizard.LargeOptionButton
            icon={WizardIcons.ReturningClientsIcon}
            caption="Select this option if the clients you want to add binders for are in your tax software."
            onClick={() => send({ type: 'clientKindSelected', clientKind: 'returning' })}
          >
            Returning clients
          </Wizard.LargeOptionButton>

          <Wizard.LargeOptionButton
            icon={WizardIcons.NewClientsIcon}
            caption="Select this option if the clients you want to add binders for are NOT in your tax software."
            onClick={() => send({ type: 'clientKindSelected', clientKind: 'new' })}
          >
            New clients
          </Wizard.LargeOptionButton>
        </Wizard.OptionsColumn>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function FolderImportTaxAppSelector({ state, send, onClose }: PageProps<'taxAppSelector'>) {
  return (
    <Wizard.Scaffold
      breadcrumb={match(state.flow)
        .with('normal', () => 'Generate binders')
        .with(P.union('personalized-checklists', 'personalized-pdf'), (flow) =>
          `Generate ${clientPacketsFlowDefinition(flow).plural}`
        )
        .exhaustive()}
      bottomNav={
        state.flow == 'normal' && (
          <Wizard.BottomNav>
            <Button variant="default" onClick={() => send({ type: 'backRequested' })}>
              Back
            </Button>
          </Wizard.BottomNav>
        )
      }
      onClose={onClose}
    >
      <Wizard.Main
        title="Select your tax software"
        subtitle={match(state.flow)
          .with(P.union('personalized-checklists', 'personalized-pdf'), (flow) =>
            `StanfordTax generates ${clientPacketsFlowDefinition(flow).descriptivePlural} using prior-year data in your tax software.`
          )
          .with(
            'normal',
            () =>
              'We use prior year tax return data to personalize each binder. Every binder comes with a client checklist and organizer.'
          )
          .exhaustive()}
      >
        <Wizard.OptionsGrid>
          {TAX_APPS_IMPORT_OPTIONS.map((app) => {
            return (
              <Wizard.OptionButton
                key={app.id}
                icon={<img className="size-14" src={`/logos/${app.id}.png`} />}
                onClick={() => send({ type: 'taxAppSelected', taxAppId: app.id })}
              >
                {app.name}
              </Wizard.OptionButton>
            )
          })}

          {state.flow == 'normal' && (
            <Wizard.OptionButton
              icon={<WizardIcons.EllipsisCircleIcon className="size-14" />}
              onClick={() => send({ type: 'otherSoftwareSelected' })}
            >
              Other software
            </Wizard.OptionButton>
          )}
        </Wizard.OptionsGrid>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function SelectAddNewClientsMethod({
  send,
  onClose,
  onBack
}: PageProps<'newClientsImportMethodSelector'>) {
  return (
    <Wizard.Scaffold
      breadcrumb="Generate binders"
      bottomNav={
        <Wizard.BottomNav>
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
        </Wizard.BottomNav>
      }
      onClose={onClose}
    >
      <Wizard.Main title="How would you like to generate the binders?">
        <Wizard.OptionsColumn>
          <Wizard.LargeOptionButton
            icon={WizardIcons.OneByOneIcon}
            caption="Select this option if you just want to generate a single binder."
            onClick={() => send({ type: 'newClientMethodSelected', method: 'one-by-one' })}
          >
            One by one
          </Wizard.LargeOptionButton>

          <Wizard.LargeOptionButton
            icon={WizardIcons.ReturningClientsIcon}
            caption="Select this option if you'd like to generate many binders in bulk."
            onClick={() => send({ type: 'newClientMethodSelected', method: 'bulk-csv' })}
          >
            In Bulk via CSV
          </Wizard.LargeOptionButton>
        </Wizard.OptionsColumn>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function UploadFolderImport({
  state,
  send,
  onClose
}: {
  state: CreateFolderImportUploadFile
  send: (message: CreateFolderImportMessage) => void
  onClose: () => void
}) {
  const captureBrowserResource = useCaptureBrowserResource()

  return (
    <UploadFolderImportWizard
      source={state.source}
      flow={state.flow}
      onBack={() => send({ type: 'backRequested' })}
      onClose={onClose}
      uploadState={state.uploadStatus}
      onDrop={(dataTransfer: DataTransfer) => {
        send(
          requestFolderImport({
            upload: captureBrowserResource(captureUpload(dataTransfer))
          })
        )
      }}
    />
  )
}
